import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  RefreshCw,
  Search,
  ShieldAlert,
  Trash2,
  UserRoundCog,
  X
} from 'lucide-react';
import { db } from '../../core/firebase/firebase';
import { useAuth } from '../../core/auth/AuthProvider';

const text = (value) => String(value ?? '').trim();

function displayName(account) {
  return text(account.fullname || account.fullName || account.displayName || account.name) || 'Compte sans nom';
}

function accountRole(account) {
  if (account.accountType === 'parent' || account.role === 'parent') return 'Parent';
  return text(account.adminRole || account.role || account.accountType) || 'Utilisateur';
}

function isDisabled(account) {
  return account.status === 'disabled' || account.status === 'blocked' || account.disabled === true;
}

async function collectByField(collectionName, field, value) {
  if (!value) return [];
  const snapshot = await getDocs(query(collection(db, collectionName), where(field, '==', value)));
  return snapshot.docs;
}

async function deleteInBatches(documentRefs) {
  const unique = Array.from(new Map(documentRefs.map((ref) => [ref.path, ref])).values());

  for (let index = 0; index < unique.length; index += 450) {
    const batch = writeBatch(db);
    unique.slice(index, index + 450).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
}

export default function AccountManagementPanel() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState('');
  const [target, setTarget] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [deleteLinkedData, setDeleteLinkedData] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    return onSnapshot(
      collection(db, 'users'),
      (snapshot) => {
        setAccounts(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      },
      (err) => {
        console.error(err);
        setError('Impossible de charger les comptes.');
      }
    );
  }, []);

  const visibleAccounts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return accounts
      .filter((account) => account.id !== user?.uid)
      .filter((account) => {
        if (!needle) return true;
        return [
          displayName(account),
          account.email,
          account.phone,
          account.username,
          account.id,
          accountRole(account)
        ].some((value) => text(value).toLowerCase().includes(needle));
      })
      .sort((a, b) => displayName(a).localeCompare(displayName(b), 'fr'));
  }, [accounts, search, user?.uid]);

  async function changeStatus(account, disabled) {
    if (account.id === user?.uid) return;
    setBusyId(account.id);
    setError('');

    try {
      await updateDoc(doc(db, 'users', account.id), {
        status: disabled ? 'disabled' : 'approved',
        disabled,
        disabledAt: disabled ? serverTimestamp() : null,
        updatedAt: serverTimestamp()
      });

      setMessage(disabled ? 'Le compte est désactivé.' : 'Le compte est réactivé.');
      window.setTimeout(() => setMessage(''), 2500);
    } catch (err) {
      console.error(err);
      setError('Impossible de modifier le statut du compte.');
    } finally {
      setBusyId('');
    }
  }

  async function permanentlyRemove() {
    if (!target || confirmation !== 'SUPPRIMER') return;
    if (target.id === user?.uid) return;

    setBusyId(target.id);
    setError('');

    try {
      const refs = [doc(db, 'users', target.id)];
      const parentId = target.parentId || null;

      if (deleteLinkedData && parentId) {
        refs.push(doc(db, 'parents', parentId));

        const studentDocs = [
          ...(await collectByField('students', 'parentId', parentId)),
          ...(await collectByField('students', 'primaryParentId', parentId)),
          ...(await getDocs(query(collection(db, 'students'), where('parentIds', 'array-contains', parentId)))).docs
        ];

        const uniqueStudentDocs = Array.from(
          new Map(studentDocs.map((item) => [item.id, item])).values()
        );
        const studentIds = [];

        for (const studentDoc of uniqueStudentDocs) {
          const student = studentDoc.data();
          const linkedParents = Array.isArray(student.parentIds)
            ? student.parentIds.filter(Boolean)
            : [];
          const remainingParents = linkedParents.filter((id) => id !== parentId);

          if (remainingParents.length > 0) {
            await updateDoc(studentDoc.ref, {
              parentIds: remainingParents,
              primaryParentId:
                student.primaryParentId === parentId
                  ? remainingParents[0]
                  : student.primaryParentId || remainingParents[0],
              parentId:
                student.parentId === parentId
                  ? remainingParents[0]
                  : student.parentId || remainingParents[0],
              updatedAt: serverTimestamp()
            });
          } else {
            refs.push(studentDoc.ref);
            studentIds.push(studentDoc.id);
          }
        }

        const parentCollections = [
          'contracts',
          'payments',
          'familyLedger',
          'notifications',
          'reclamations',
          'feedbacks'
        ];

        for (const collectionName of parentCollections) {
          const docs = await collectByField(collectionName, 'parentId', parentId);
          docs.forEach((item) => refs.push(item.ref));
        }

        for (const studentId of studentIds) {
          const studentCollections = ['contracts', 'payments', 'reclamations', 'feedbacks'];
          for (const collectionName of studentCollections) {
            const docs = await collectByField(collectionName, 'studentId', studentId);
            docs.forEach((item) => refs.push(item.ref));
          }
        }
      }

      await deleteInBatches(refs);

      setTarget(null);
      setConfirmation('');
      setMessage(
        'Le profil et les données sélectionnées ont été supprimés de la plateforme. La suppression définitive de l’accès doit encore être finalisée côté administration.'
      );
    } catch (err) {
      console.error(err);
      setError(`Suppression impossible : ${err.message}`);
    } finally {
      setBusyId('');
    }
  }

  return (
    <section className="panel account-management-panel">
      <div className="panel-heading account-management-heading">
        <div>
          <p className="eyebrow">GÉRANCE</p>
          <h2>Gestion des comptes</h2>
          <p className="muted">Désactiver un accès ou supprimer les données d’un compte de test.</p>
        </div>
        <UserRoundCog size={26} />
      </div>

      {message && <div className="success-note"><CheckCircle2 size={18}/>{message}</div>}
      {error && <div className="alert alert-error"><AlertTriangle size={18}/>{error}</div>}

      <div className="account-management-warning">
        <ShieldAlert size={19}/>
        <span>
          La suppression d’un profil peut nécessiter une étape supplémentaire pour retirer définitivement son accès. Vérifiez toujours le résultat après l’opération.
        </span>
      </div>

      <label className="student-search account-search">
        <Search size={17}/>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Nom, email, téléphone, UID…"
        />
      </label>

      <div className="account-management-list">
        {visibleAccounts.map((account) => (
          <article key={account.id} className="account-management-row">
            <div className="account-avatar">{displayName(account).charAt(0).toUpperCase()}</div>
            <div className="account-copy">
              <strong>{displayName(account)}</strong>
              <span>{account.email || account.username || 'Aucun identifiant'} · {accountRole(account)}</span>
              <small>{account.id}</small>
            </div>
            <span className={`account-state ${isDisabled(account) ? 'disabled' : 'active'}`}>
              {isDisabled(account) ? 'Désactivé' : 'Actif'}
            </span>
            <div className="account-actions">
              <button
                type="button"
                className="button button-secondary"
                disabled={busyId === account.id}
                onClick={() => changeStatus(account, !isDisabled(account))}
              >
                {busyId === account.id ? <RefreshCw size={16}/> : <Ban size={16}/>} 
                {isDisabled(account) ? 'Réactiver' : 'Désactiver'}
              </button>
              <button
                type="button"
                className="button account-delete-button"
                disabled={busyId === account.id}
                onClick={() => {
                  setTarget(account);
                  setConfirmation('');
                  setDeleteLinkedData(true);
                }}
              >
                <Trash2 size={16}/>
                Supprimer
              </button>
            </div>
          </article>
        ))}
        {!visibleAccounts.length && <div className="compact-empty">Aucun compte trouvé.</div>}
      </div>

      {target && (
        <div className="modal-layer">
          <section className="panel account-delete-modal">
            <header>
              <div>
                <p className="eyebrow">ACTION IRRÉVERSIBLE</p>
                <h2>Supprimer {displayName(target)}</h2>
              </div>
              <button type="button" className="drawer-close" onClick={() => setTarget(null)} aria-label="Fermer">
                <X size={20}/>
              </button>
            </header>

            <div className="account-delete-body">
              <div className="alert alert-error">
                <AlertTriangle size={19}/>
                Cette action supprime le profil de la plateforme. L’accès associé peut nécessiter une suppression définitive séparée.
              </div>

              {target.parentId && (
                <label className="account-delete-check">
                  <input
                    type="checkbox"
                    checked={deleteLinkedData}
                    onChange={(event) => setDeleteLinkedData(event.target.checked)}
                  />
                  Supprimer aussi le parent, les élèves et les données de test liées.
                </label>
              )}

              <label>
                Tapez <strong>SUPPRIMER</strong> pour confirmer
                <input
                  value={confirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                  autoFocus
                />
              </label>

              <div className="family-form-footer">
                <button type="button" className="button button-secondary" onClick={() => setTarget(null)}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="button account-delete-button"
                  disabled={confirmation !== 'SUPPRIMER' || busyId === target.id}
                  onClick={permanentlyRemove}
                >
                  <Trash2 size={16}/>
                  {busyId === target.id ? 'Suppression…' : 'Supprimer de la plateforme'}
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
